import React, { useState, useEffect, useMemo } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import DropzoneArea from '../DropzoneArea';
import {
    loadFinbotSettings,
    saveFinbotSettings,
    loadFinbotClientsCache,
    saveFinbotClientsCache,
    matchFinbotClientByCaseId,
} from './finbotImportStorage';

const FinbotAccountCardsImportModal = ({
    open,
    onClose,
    previewFinbotAccountCards,
    commitFinbotAccountCards,
    previewFinbotAccountCardsApi,
    commitFinbotAccountCardsApi,
    fetchFinbotClients,
    createMissingPalCode,
    direction,
    t,
    palCodeCategoryOptions = [],
    palCodes = [],
    Constants = {},
    client,
    user
}) => {
    const customStyles = {
        container: (provided) => ({
            ...provided,
            width: '100%',
            boxSizing: 'border-box !important'
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            width: '100% !important',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: 'none',
            boxShadow: 'none',
            padding: '0 10px',
            textAlign: 'left',
            boxSizing: 'border-box !important',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black'
        }),
        singleValue: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        menu: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#F9AA2A'
                : state.isFocused
                    ? '#ebebeb'
                    : null,
            color: 'black',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? 'darkgray' : 'black',
            '&:hover': {
                color: 'darkgray',
            },
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 2002,
        }),
    };

    // Format PalCodeType options
    const palCodeTypeOptions = (Constants.PalCodeType || []).map(type => ({
        label: type,
        value: type
    }));

    // Format PalCodeCategory options
    const palCodeCategorySelectOptions = palCodeCategoryOptions.map(cat => ({
        label: cat.palCodeCategoryId,
        value: cat.palCodeCategoryId
    }));

    const [importMode, setImportMode] = useState('file'); // 'file' | 'api'
    const [templateVersion, setTemplateVersion] = useState(1); // 1 = classic Finbot cards; 2 = customers list
    const [finbotAccountCardsFile, setFinbotAccountCardsFile] = useState(null);
    const [finbotAccountCardsEntries, setFinbotAccountCardsEntries] = useState([]);
    const [finbotAccountCardsMissingPalCodes, setFinbotAccountCardsMissingPalCodes] = useState([]);
    const [finbotAccountCardsPreviewDone, setFinbotAccountCardsPreviewDone] = useState(false);
    const [finbotAccountCardsProcessing, setFinbotAccountCardsProcessing] = useState(false);
    const [finbotAccountCardsParsedRows, setFinbotAccountCardsParsedRows] = useState(0);

    // API-mode credentials: prefilled from localStorage so they carry across clients and modals.
    const storedSettings = useMemo(() => loadFinbotSettings(), []);
    const [finbotToken, setFinbotToken] = useState(storedSettings.token || '');
    const [finbotUserAccountId, setFinbotUserAccountId] = useState('');
    const [subdomain, setSubdomain] = useState(storedSettings.subdomain || '');

    // Finbot client picker (the loaded list is cached so auto-matching works without re-loading)
    const [clients, setClients] = useState(() => loadFinbotClientsCache());
    const [selectedClient, setSelectedClient] = useState(null);
    const [loadingClients, setLoadingClients] = useState(false);

    // The id used for the actual import: the selected client, falling back to the typed id.
    const effectiveAccountId = (selectedClient?.value ?? String(finbotUserAccountId)).trim();
    // Loading the client list only needs the token + subdomain: the account id is what we are
    // trying to discover from that list, so it must not be required here.
    const credentialsReady = !!(finbotToken.trim() && subdomain.trim());

    const clientOptions = clients.map(c => ({
        value: String(c.id),
        label: `${c.name || ''}${c.companyId ? ` - ${c.companyId}` : ''}`,
    }));

    // Persist credentials whenever they change, so the next client/modal opens prefilled.
    useEffect(() => {
        saveFinbotSettings({ token: finbotToken, subdomain });
    }, [finbotToken, subdomain]);

    // Cache the loaded client list for reuse across clients.
    useEffect(() => {
        saveFinbotClientsCache(clients);
    }, [clients]);

    // Auto-resolve the Finbot account id for the current client by matching its caseId against the
    // Finbot client companyId, so the user does not have to pick it manually.
    useEffect(() => {
        if (importMode !== 'api' || !clients.length) return;
        const match = matchFinbotClientByCaseId(clients, client?.caseId);
        if (match && match.id != null) {
            setSelectedClient({
                value: String(match.id),
                label: `${match.name || ''}${match.companyId ? ` - ${match.companyId}` : ''}`,
            });
            setFinbotUserAccountId(String(match.id));
        }
    }, [clients, client?.caseId, importMode]);

    const handleLoadClients = async () => {
        if (!credentialsReady || !fetchFinbotClients) return;
        try {
            setLoadingClients(true);
            const list = await fetchFinbotClients({
                finbotToken: finbotToken.trim(),
                finbotUserAccountId: String(finbotUserAccountId).trim(),
                subdomain: subdomain.trim(),
            });
            setClients(Array.isArray(list) ? list : []);
        } catch {
            setClients([]);
        } finally {
            setLoadingClients(false);
        }
    };

    const buildApiParams = () => ({
        finbotToken: finbotToken.trim(),
        finbotUserAccountId: effectiveAccountId,
        subdomain: subdomain.trim(),
    });

    const apiFieldsReady = !!(finbotToken.trim() && effectiveAccountId && subdomain.trim());
    const inputReady = importMode === 'api' ? apiFieldsReady : !!finbotAccountCardsFile;

    // State for creating missing PalCodes
    const [editingPalCode, setEditingPalCode] = useState(null);
    const [bulkMode, setBulkMode] = useState(false);
    const [selectedPalCodesForBulk, setSelectedPalCodesForBulk] = useState([]);
    const [palCodeForm, setPalCodeForm] = useState({
        palCodeId: '',
        palCodeType: '',
        palCodeCategory: null
    });
    const [bulkForm, setBulkForm] = useState({
        palCodeType: '',
        palCodeCategory: null
    });

    // Calculate next orderNumber for a given category
    const calculateNextOrderNumber = (category) => {
        if (!category || palCodes.length === 0) return 100;

        const palCodesInCategory = palCodes.filter(
            pc => pc.palCodeCategory === category
        );

        const maxOrderNumber = palCodesInCategory.reduce((max, pc) => {
            const orderNum = pc.orderNumber || 0;
            return Math.max(max, orderNum);
        }, 0);

        return maxOrderNumber > 0 ? maxOrderNumber + 100 : 100;
    };

    const handleFinbotAccountCardsDrop = files => {
        if (files?.length) setFinbotAccountCardsFile(files[0]);
    };

    const handleFinbotAccountCardsPreview = async () => {
        if (!inputReady) return;

        try {
            setFinbotAccountCardsProcessing(true);
            const res = importMode === 'api'
                ? await previewFinbotAccountCardsApi(buildApiParams())
                : await previewFinbotAccountCards(finbotAccountCardsFile, templateVersion);
            const accountCards = res?.accountCards ?? [];
            const missingPalCodes = res?.missingPalCodes ?? [];
            const parsedRows = res?.parsedRows ?? 0;

            setFinbotAccountCardsEntries(accountCards);
            setFinbotAccountCardsMissingPalCodes(missingPalCodes);
            setFinbotAccountCardsParsedRows(parsedRows);
            setFinbotAccountCardsPreviewDone(true);
        } catch (err) {
            console.error('Finbot account cards preview failed:', err);
            setFinbotAccountCardsPreviewDone(false);
        } finally {
            setFinbotAccountCardsProcessing(false);
        }
    };

    const handleCreatePalCode = async (palCodeName) => {
        if (!palCodeForm.palCodeId || !palCodeForm.palCodeType) {
            return;
        }

        try {
            setFinbotAccountCardsProcessing(true);

            const calculatedOrderNumber = palCodeForm.palCodeCategory
                ? calculateNextOrderNumber(palCodeForm.palCodeCategory)
                : 100;

            const newPalCode = {
                palCodeId: palCodeForm.palCodeId,
                palCodeType: palCodeForm.palCodeType,
                palCodeCategory: palCodeForm.palCodeCategory,
                orderNumber: calculatedOrderNumber,
                accountantId: user.accountantId,
            };

            const response = await createMissingPalCode(newPalCode);
            if (!response.ok) {
                const errorText = await response.text();
                console.error("PalCode creation failed:", errorText);
                throw new Error(errorText);
            }

            // Remove from missing list
            setFinbotAccountCardsMissingPalCodes(prev =>
                prev.filter(pc => pc !== palCodeName)
            );

            // Reset form
            setEditingPalCode(null);
            setPalCodeForm({
                palCodeId: '',
                palCodeType: '',
                palCodeCategory: null
            });
        } catch (err) {
            console.error('PalCode creation error:', err);
            throw err;
        } finally {
            setFinbotAccountCardsProcessing(false);
        }
    };

    const handleBulkCreatePalCodes = async () => {
        if (!bulkForm.palCodeType || selectedPalCodesForBulk.length === 0) {
            return;
        }

        try {
            setFinbotAccountCardsProcessing(true);

            const baseOrderNumber = bulkForm.palCodeCategory
                ? calculateNextOrderNumber(bulkForm.palCodeCategory)
                : 100;

            const createPromises = selectedPalCodesForBulk.map((palCodeName, index) => {
                const newPalCode = {
                    palCodeId: palCodeName,
                    palCodeType: bulkForm.palCodeType,
                    palCodeCategory: bulkForm.palCodeCategory,
                    orderNumber: baseOrderNumber + (index * 100),
                    accountantId: user.accountantId,
                };
                return createMissingPalCode(newPalCode);
            });

            const responses = await Promise.all(createPromises);
            const failed = responses.filter(r => !r.ok);

            if (failed.length > 0) {
                console.error("Some PalCodes failed to create");
                throw new Error(`${failed.length} PalCodes failed to create`);
            }

            // Remove created PalCodes from missing list
            setFinbotAccountCardsMissingPalCodes(prev =>
                prev.filter(pc => !selectedPalCodesForBulk.includes(pc))
            );

            // Reset bulk mode
            setBulkMode(false);
            setSelectedPalCodesForBulk([]);
            setBulkForm({
                palCodeType: '',
                palCodeCategory: null
            });
        } catch (err) {
            console.error('Bulk PalCode creation error:', err);
            throw err;
        } finally {
            setFinbotAccountCardsProcessing(false);
        }
    };

    const handleFinbotAccountCardsCommit = async () => {
        if (!inputReady) return;

        try {
            setFinbotAccountCardsProcessing(true);

            const res = importMode === 'api'
                ? await commitFinbotAccountCardsApi(buildApiParams())
                : await commitFinbotAccountCards(finbotAccountCardsFile, templateVersion);

            if (res && res.errorMessages && res.errorMessages.length > 0) {
                console.error('Import errors:', res.errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Finbot account cards commit error:', err);
        } finally {
            setFinbotAccountCardsProcessing(false);
        }
    };

    const handleClose = () => {
        setFinbotAccountCardsFile(null);
        setImportMode('file');
        setTemplateVersion(1);
        // Keep token / subdomain / loaded clients / selection so they carry to the next client
        // (persisted in localStorage); only the per-import view state is reset below.
        setLoadingClients(false);
        setFinbotAccountCardsPreviewDone(false);
        setFinbotAccountCardsEntries([]);
        setFinbotAccountCardsMissingPalCodes([]);
        setFinbotAccountCardsParsedRows(0);
        setEditingPalCode(null);
        setPalCodeForm({
            palCodeId: '',
            palCodeType: '',
            palCodeCategory: null
        });
        onClose();
    };

    const startEditingPalCode = (palCodeName) => {
        setEditingPalCode(palCodeName);
        setPalCodeForm({
            palCodeId: palCodeName,
            palCodeType: '',
            palCodeCategory: null
        });
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
            <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: finbotAccountCardsPreviewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            {t('ImportFinbotAccountCards') || 'ייבוא כרטיסי חשבון מ-Finbot'}
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/* Import mode toggle: file vs API */}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Button
                                size="small"
                                variant={importMode === 'file' ? 'contained' : 'outlined'}
                                onClick={() => setImportMode('file')}
                                disabled={finbotAccountCardsProcessing}
                            >
                                {t('ImportFromFile') || 'מקובץ'}
                            </Button>
                            <Button
                                size="small"
                                variant={importMode === 'api' ? 'contained' : 'outlined'}
                                onClick={() => setImportMode('api')}
                                disabled={finbotAccountCardsProcessing}
                            >
                                {t('ImportFromApi') || 'מ-API'}
                            </Button>
                        </div>

                        {importMode === 'file' ? (
                            <div>
                                <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                                    <Button
                                        size="small"
                                        variant={templateVersion === 1 ? 'contained' : 'outlined'}
                                        onClick={() => {
                                            setTemplateVersion(1);
                                            setFinbotAccountCardsPreviewDone(false);
                                            setFinbotAccountCardsEntries([]);
                                            setFinbotAccountCardsMissingPalCodes([]);
                                            setFinbotAccountCardsParsedRows(0);
                                        }}
                                        disabled={finbotAccountCardsProcessing}
                                    >
                                        {t('FinbotAccountCardsTemplateV1') || 'גרסה 1 — כרטיסי חשבון'}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant={templateVersion === 2 ? 'contained' : 'outlined'}
                                        onClick={() => {
                                            setTemplateVersion(2);
                                            setFinbotAccountCardsPreviewDone(false);
                                            setFinbotAccountCardsEntries([]);
                                            setFinbotAccountCardsMissingPalCodes([]);
                                            setFinbotAccountCardsParsedRows(0);
                                        }}
                                        disabled={finbotAccountCardsProcessing}
                                    >
                                        {t('FinbotAccountCardsTemplateV2') || 'גרסה 2 — רשימת לקוחות'}
                                    </Button>
                                </div>
                                <h5>{t('UploadFile')} (.xlsx/.xls)</h5>
                                <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                                    {templateVersion === 2
                                        ? (t('FinbotAccountCardsFormatV2') || 'מספר סידורי, שם לקוח-מוצג על המסמך, כתובת מייל, טלפון, ת.ז./ מספר עוסק — עדכון לפי שם מדויק או פתיחת כרטיס חדש')
                                        : (t('FinbotAccountCardsFormat') || 'עמודה B: מספר כרטיס, עמודה C: שם לקוח, עמודה D: סוג כרטיס, עמודה H: מספר מע"מ')}
                                </p>
                                <DropzoneArea
                                    key={finbotAccountCardsFile ? finbotAccountCardsFile.name : 'finbot-account-cards-empty'}
                                    onDrop={handleFinbotAccountCardsDrop}
                                    accept=".xls,.xlsx"
                                    filesLimit={1}
                                />
                                {finbotAccountCardsFile && (
                                    <div style={{ marginTop: 8 }}>
                                        <span>{t('FileSelected')}: {finbotAccountCardsFile.name}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <TextField
                                    label={t('FinbotToken') || 'Finbot Token'}
                                    value={finbotToken}
                                    onChange={(e) => setFinbotToken(e.target.value)}
                                    size="small"
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    placeholder={t('FinbotTokenPlaceholder') || 'הדבק כאן את ה-JWT מ-Finbot'}
                                />
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                    <TextField
                                        label={t('FinbotUserAccountId') || 'Finbot Account Id'}
                                        value={finbotUserAccountId}
                                        onChange={(e) => setFinbotUserAccountId(e.target.value)}
                                        size="small"
                                        style={{ flex: 1, minWidth: 160 }}
                                    />
                                    <TextField
                                        label={t('FinbotSubdomain') || 'Subdomain'}
                                        value={subdomain}
                                        onChange={(e) => setSubdomain(e.target.value)}
                                        size="small"
                                        style={{ flex: 1, minWidth: 160 }}
                                        placeholder="zuab"
                                    />
                                </div>

                                {/* Client picker: load and search the accountant's Finbot clients */}
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleLoadClients}
                                        disabled={!credentialsReady || loadingClients}
                                    >
                                        {loadingClients ? (t('Processing') || 'Processing') : (t('LoadFinbotClients') || 'טען לקוחות')}
                                    </Button>
                                    <div style={{ flex: 1, minWidth: 220 }}>
                                        <Select
                                            placeholder={t('SelectFinbotClient') || 'חפש/בחר לקוח'}
                                            options={clientOptions}
                                            value={selectedClient}
                                            isLoading={loadingClients}
                                            isClearable
                                            onChange={(option) => {
                                                setSelectedClient(option);
                                                if (option?.value) setFinbotUserAccountId(option.value);
                                            }}
                                            noOptionsMessage={() => t('NoFinbotClients') || 'אין לקוחות (טען קודם)'}
                                            styles={customStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handleFinbotAccountCardsPreview}
                            disabled={!inputReady || finbotAccountCardsProcessing}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!finbotAccountCardsPreviewDone || finbotAccountCardsProcessing || finbotAccountCardsMissingPalCodes.length > 0}
                            onClick={handleFinbotAccountCardsCommit}
                        >
                            {finbotAccountCardsProcessing ? t('Processing') : t('Import')}
                        </Button>
                    </div>

                    {finbotAccountCardsPreviewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: '14px', color: '#666' }}>
                                    {t('ParsedRows') || 'שורות שנקראו'}: <strong>{finbotAccountCardsParsedRows}</strong> |
                                    {' '}{t('AccountCards') || 'כרטיסי חשבון'}: <strong>{finbotAccountCardsEntries.length}</strong>
                                </p>
                            </div>

                            {/* Missing PalCodes Section */}
                            {finbotAccountCardsMissingPalCodes.length > 0 && (
                                <div style={{ marginBottom: 25, padding: 15, backgroundColor: '#fff3cd', borderRadius: 5, border: '1px solid #ffc107' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                                        <div>
                                            <h5 style={{ color: '#856404', margin: 0 }}>
                                                {t('MissingPalCodes') || 'חשבונות ראשיים חסרים'}
                                            </h5>
                                            <p style={{ fontSize: '14px', color: '#856404', margin: '5px 0 0 0' }}>
                                                {t('MissingPalCodesDescription') || 'יש ליצור את החשבונות הראשיים הבאים לפני הייבוא:'}
                                            </p>
                                        </div>
                                        <Button
                                            variant={bulkMode ? "contained" : "outlined"}
                                            size="small"
                                            onClick={() => {
                                                setBulkMode(!bulkMode);
                                                setSelectedPalCodesForBulk([]);
                                                if (!bulkMode) {
                                                    setBulkForm({
                                                        palCodeType: '',
                                                        palCodeCategory: null
                                                    });
                                                }
                                            }}
                                        >
                                            {bulkMode ? (t('CancelBulk') || 'ביטול יצירה מרובה') : (t('BulkCreate') || 'יצירה מרובה')}
                                        </Button>
                                    </div>

                                    {/* Bulk Creation Form */}
                                    {bulkMode && (
                                        <div style={{ marginBottom: 20, padding: 15, backgroundColor: '#f0f0f0', borderRadius: 5 }}>
                                            <h6 style={{ marginBottom: 10 }}>{t('BulkCreatePalCodes') || 'יצירת חשבונות ראשיים מרובים'}</h6>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                        {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Select
                                                        placeholder={t('Select') || 'בחר'}
                                                        options={palCodeTypeOptions}
                                                        value={palCodeTypeOptions.find(opt => opt.value === bulkForm.palCodeType) || null}
                                                        onChange={(selectedOption) => {
                                                            setBulkForm({ ...bulkForm, palCodeType: selectedOption?.value || '' });
                                                        }}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </div>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                        {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                    </label>
                                                    <Select
                                                        placeholder={t('None') || 'ללא'}
                                                        options={palCodeCategorySelectOptions}
                                                        value={palCodeCategorySelectOptions.find(opt => opt.value === bulkForm.palCodeCategory) || null}
                                                        onChange={(selectedOption) => {
                                                            setBulkForm({ ...bulkForm, palCodeCategory: selectedOption?.value || null });
                                                        }}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </div>
                                                <div style={{ marginTop: 10 }}>
                                                    <p style={{ fontSize: '12px', color: '#666', marginBottom: 5 }}>
                                                        {t('SelectedPalCodes') || 'נבחרו'}: {selectedPalCodesForBulk.length} / {finbotAccountCardsMissingPalCodes.length}
                                                    </p>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={handleBulkCreatePalCodes}
                                                        disabled={finbotAccountCardsProcessing || !bulkForm.palCodeType || selectedPalCodesForBulk.length === 0}
                                                        fullWidth
                                                    >
                                                        {t('CreateSelected') || `צור ${selectedPalCodesForBulk.length} חשבונות`}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {finbotAccountCardsMissingPalCodes.map((palCodeName, idx) => (
                                        <div key={idx} style={{ marginBottom: 15, padding: 10, backgroundColor: '#fff', borderRadius: 3 }}>
                                            {editingPalCode === palCodeName ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                    <TextField
                                                        label={t('PalCodeId') || 'מזהה חשבון ראשי'}
                                                        value={palCodeForm.palCodeId}
                                                        onChange={(e) => setPalCodeForm({ ...palCodeForm, palCodeId: e.target.value })}
                                                        size="small"
                                                        fullWidth
                                                        disabled
                                                    />
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                            {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                        </label>
                                                        <Select
                                                            placeholder={t('Select') || 'בחר'}
                                                            options={palCodeTypeOptions}
                                                            value={palCodeTypeOptions.find(opt => opt.value === palCodeForm.palCodeType) || null}
                                                            onChange={(selectedOption) => {
                                                                setPalCodeForm({ ...palCodeForm, palCodeType: selectedOption?.value || '' });
                                                            }}
                                                            styles={customStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                            {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                        </label>
                                                        <Select
                                                            placeholder={t('None') || 'ללא'}
                                                            options={palCodeCategorySelectOptions}
                                                            value={palCodeCategorySelectOptions.find(opt => opt.value === palCodeForm.palCodeCategory) || null}
                                                            onChange={(selectedOption) => {
                                                                setPalCodeForm({ ...palCodeForm, palCodeCategory: selectedOption?.value || null });
                                                            }}
                                                            styles={customStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                        />
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 10 }}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => handleCreatePalCode(palCodeName)}
                                                            disabled={finbotAccountCardsProcessing || !palCodeForm.palCodeId || !palCodeForm.palCodeType}
                                                        >
                                                            {t('create') || 'צור'}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => {
                                                                setEditingPalCode(null);
                                                                setPalCodeForm({
                                                                    palCodeId: '',
                                                                    palCodeType: '',
                                                                    palCodeCategory: null
                                                                });
                                                            }}
                                                        >
                                                            {t('Cancel') || 'ביטול'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        {bulkMode && (
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedPalCodesForBulk.includes(palCodeName)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedPalCodesForBulk(prev => [...prev, palCodeName]);
                                                                    } else {
                                                                        setSelectedPalCodesForBulk(prev => prev.filter(pc => pc !== palCodeName));
                                                                    }
                                                                }}
                                                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                            />
                                                        )}
                                                        <span style={{ fontWeight: 'bold' }}>{palCodeName}</span>
                                                    </div>
                                                    {!bulkMode && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => startEditingPalCode(palCodeName)}
                                                        >
                                                            {t('CreatePalCode') || 'צור חשבון ראשי'}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Account Cards Table */}
                            {finbotAccountCardsEntries.length === 0 ? (
                                <p style={{ color: '#666' }}>{t('NoAccountCardsFound') || 'לא נמצאו כרטיסי חשבון'}</p>
                            ) : (
                                <>
                                    <h5>{t('AccountCards') || 'כרטיסי חשבון'}</h5>
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardId') || 'מזהה כרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardName') || 'שם הכרטיס'}</th>
                                                {templateVersion === 2 && importMode === 'file' ? (
                                                    <>
                                                        <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('Email') || 'מייל'}</th>
                                                        <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('PhoneNumber') || 'טלפון'}</th>
                                                        <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('AccountCaseId') || 'ת.ז./עוסק'}</th>
                                                    </>
                                                ) : (
                                                    <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('PalCode') || 'חשבון ראשי'}</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {finbotAccountCardsEntries.map((entry, i) => {
                                                const isMissingPalCode = finbotAccountCardsMissingPalCodes.includes(entry.palCode);
                                                const showV2Cols = templateVersion === 2 && importMode === 'file';
                                                return (
                                                    <tr key={i} style={{
                                                        backgroundColor: i % 2 === 0 ? '#fff' : '#f9f9f9',
                                                        ...(isMissingPalCode && !showV2Cols ? { backgroundColor: '#fff3cd' } : {})
                                                    }}>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.cardId || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.cardName || ''}</td>
                                                        {showV2Cols ? (
                                                            <>
                                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.email || ''}</td>
                                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.phoneNumber || ''}</td>
                                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.accountCaseId || ''}</td>
                                                            </>
                                                        ) : (
                                                            <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left', color: isMissingPalCode ? '#856404' : 'inherit' }}>
                                                                {entry.palCode || ''}
                                                                {isMissingPalCode && <span style={{ color: '#dc3545', marginLeft: 5 }}>*</span>}
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FinbotAccountCardsImportModal;
